import { SalvButton, SalvModal, SalvDropdown, Size, SalvTagsInput } from '@salvapp/ui-components';
import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { bridgeSettingsApi, personInternalApi } from '../../apis';
import { convertToDropdownOption, getPersonDisplayName } from '../../common/helper';
import { AttributesRecipientMapping } from './BridgeRecipientConfigurations';
import { Form, Formik, FormikHelpers, FormikProps, FormikState } from 'formik';
import * as Yup from 'yup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faCircleXmark, faCodeCompare } from '@fortawesome/pro-solid-svg-icons';
import { parseErrorMessage } from '../../common/errors/helpers';
import { ApiMappings } from '../../api';
import { useQueryBridgeFriends } from '../bridgeActions';

interface BridgeRecipientConfigurationModalProps {
  fieldTypes: string[];
  mapping?: AttributesRecipientMapping;
  recipientMappings: AttributesRecipientMapping[];
  attributeMappings?: ApiMappings;
  onClose: () => void;
  show: boolean;
}

interface BridgeRecipientMappingFormValues {
  fieldType: string;
  selectedValue: string;
  fieldValues: string[];
}

interface ValidationResult {
  personId: string;
  isValid: boolean;
  error?: string;
  personName?: string;
}

export const BridgeRecipientConfigurationModal = ({
  fieldTypes,
  mapping,
  recipientMappings,
  attributeMappings,
  onClose,
  show,
}: BridgeRecipientConfigurationModalProps) => {
  const [validationResults, setValidationResults] = useState<ValidationResult[]>([]);
  const [isValidating, setIsValidating] = useState(false);
  const queryClient = useQueryClient();

  const findExistingMapping = (recipientName: string, fieldType: string) => {
    return recipientMappings.find((m) => m.selectedValue === recipientName && m.fieldType === fieldType);
  };

  const { data: bridgeFriends } = useQueryBridgeFriends();

  const { mutateAsync: updateRecipientMapping } = useMutation({
    mutationFn: async (values: BridgeRecipientMappingFormValues) => {
      return await bridgeSettingsApi.updateBridgeRecipientMappingConfiguration({
        internalApiBridgeRecipientMappingUpdateRequest: {
          fieldType: values.fieldType,
          fieldValues: values.fieldValues,
          selectedValue: values.selectedValue,
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bridge_recipient_mappings'] });
    },
  });

  const initialValues: BridgeRecipientMappingFormValues = {
    fieldType: mapping?.fieldType || fieldTypes[0],
    selectedValue: mapping?.selectedValue || '',
    fieldValues: mapping?.fieldValues || [],
  };

  const initialFormikState: Partial<FormikState<BridgeRecipientMappingFormValues>> = {
    values: initialValues,
    isSubmitting: false,
    isValidating: false,
  };

  const validationSchema = Yup.object().shape({
    fieldType: Yup.string().required(),
    selectedValue: Yup.string().required(),
    fieldValues: Yup.array().of(Yup.string().required()),
  });

  const validatePersonIds = async (personIds: string[]) => {
    if (personIds.length === 0) {
      setValidationResults([]);
      return;
    }

    setIsValidating(true);
    setValidationResults([]);

    const results: ValidationResult[] = [];

    for (const externalPersonId of personIds) {
      try {
        const person = await personInternalApi.findPersonByIdInternal({ personId: externalPersonId });
        results.push({
          personId: externalPersonId,
          isValid: true,
          personName: getPersonDisplayName(person, attributeMappings?.map || {}),
        });
      } catch (error) {
        const errorMessage = await parseErrorMessage(error);
        results.push({
          personId: externalPersonId,
          isValid: false,
          error: errorMessage,
        });
      }
    }

    setValidationResults(results);
    setIsValidating(false);
  };

  const submit = async (
    values: BridgeRecipientMappingFormValues,
    { resetForm }: FormikHelpers<BridgeRecipientMappingFormValues>
  ) => {
    await updateRecipientMapping(values);

    onClose();
    resetForm(initialFormikState);
    setValidationResults([]);
  };

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={submit} enableReinitialize>
      {({ resetForm, values, errors, setFieldValue }: FormikProps<BridgeRecipientMappingFormValues>) => {
        const handleCancel = () => {
          onClose();
          resetForm();
          setValidationResults([]);
        };

        const handleRecipientChange = async (options: any[]) => {
          const selectedRecipient = options[0].value;
          await setFieldValue('selectedValue', selectedRecipient);

          const existingMapping = findExistingMapping(selectedRecipient, values.fieldType);
          if (existingMapping) {
            await setFieldValue('fieldValues', existingMapping.fieldValues);
          } else {
            await setFieldValue('fieldValues', []);
          }
        };

        const handleFieldTypeChange = async (options: any[]) => {
          const selectedFieldType = options[0].value;
          await setFieldValue('fieldType', selectedFieldType);

          const existingMapping = findExistingMapping(values.selectedValue, selectedFieldType);
          if (existingMapping) {
            await setFieldValue('fieldValues', existingMapping.fieldValues);
          } else {
            await setFieldValue('fieldValues', []);
          }
        };

        const validationStatusMessage = () => {
          if (validationResults.length === 0) {
            return (
              <div className='unvalidated'>
                <FontAwesomeIcon icon={faCodeCompare} aria-label='Validate' className='m-r-4' />
                Not validated
              </div>
            );
          }

          const validCount = validationResults.filter((r) => r.isValid).length;
          const invalidCount = validationResults.filter((r) => !r.isValid).length;

          if (invalidCount === 0) {
            return (
              <div className='m-t-4'>
                <FontAwesomeIcon icon={faCircleCheck} aria-label='Validated' className='m-r-4' />
                All external person IDs are valid
              </div>
            );
          }

          return (
            <div className='failed'>
              <FontAwesomeIcon icon={faCircleXmark} aria-label='Errors' className='m-r-4' />
              {validCount} valid, {invalidCount} invalid external person IDs
            </div>
          );
        };

        const currentExistingMapping = findExistingMapping(values.selectedValue, values.fieldType);

        return (
          <>
            <SalvModal show={show} onHide={handleCancel}>
              <Form>
                <SalvModal.Header>
                  {mapping ? `Edit Configuration for ${mapping.selectedValue}` : 'Add Bridge Recipient Mapping'}
                </SalvModal.Header>
                <SalvModal.Body>
                  <SalvDropdown
                    label='Field Type'
                    options={fieldTypes.map((type) => convertToDropdownOption(type, type))}
                    value={convertToDropdownOption(values.fieldType, values.fieldType)}
                    onChange={handleFieldTypeChange}
                    placeholder='Select a field type'
                    isInvalid={!!errors.fieldType}
                    clearable={false}
                    disabled={!!mapping}
                    className='m-b-16'
                  />

                  <SalvDropdown
                    label='Recipient'
                    options={bridgeFriends.map((friend) => convertToDropdownOption(friend.name, friend.name))}
                    value={convertToDropdownOption(values.selectedValue, values.selectedValue)}
                    onChange={handleRecipientChange}
                    isMulti={false}
                    placeholder='Select a bridge friend'
                    clearable={false}
                    isInvalid={!!errors.selectedValue}
                    disabled={!!mapping}
                    className='m-b-16'
                  />

                  <SalvTagsInput
                    value={values.fieldValues}
                    onChange={(newValues) => {
                      setFieldValue('fieldValues', newValues);
                      if (values.fieldType === 'PERSON_ID') {
                        setValidationResults([]);
                      }
                    }}
                    label='Attributes'
                    placeholder='Enter attribute'
                    isInvalid={!!errors.fieldValues}
                  />

                  {currentExistingMapping && values.selectedValue && (
                    <div className='text-secondary small m-b-16'>Existing mapping found for {values.selectedValue}</div>
                  )}

                  {values.fieldType === 'PERSON_ID' && values.fieldValues.length > 0 && (
                    <div className='m-t-16 d-flex align-items-center'>
                      <SalvButton
                        variant='muted'
                        size={Size.SMALL}
                        onClick={() => validatePersonIds(values.fieldValues)}
                        disabled={isValidating}
                        label={
                          <>
                            <FontAwesomeIcon icon={faCodeCompare} aria-label='validate' className='m-r-4' />
                            Verify external person ID matches
                          </>
                        }
                        className='m-r-16'
                      />
                      <div>{validationStatusMessage()}</div>
                    </div>
                  )}

                  {values.fieldType === 'PERSON_ID' && validationResults.length > 0 && (
                    <div className='m-t-16'>
                      <div className='text-secondary m-b-4'>Validation Results:</div>
                      {validationResults.map((result, index) => (
                        <div key={index} className='m-b-8'>
                          <div className='d-flex align-items-center'>
                            <FontAwesomeIcon
                              icon={result.isValid ? faCircleCheck : faCircleXmark}
                              className={`m-r-8 ${result.isValid ? 'text-success' : 'text-danger'}`}
                            />
                            <div>
                              <div className='text-strong-s'>{result.personId}</div>
                              {result.isValid && result.personName && (
                                <div className='text-secondary text-xs'>Person name: {result.personName}</div>
                              )}
                              {!result.isValid && result.error && (
                                <div className='text-danger text-s'>{result.error}</div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </SalvModal.Body>
                <SalvModal.Footer>
                  <SalvButton label='Cancel' onClick={handleCancel} variant='muted' />

                  <SalvButton type='submit' label='Save' className='m-l-4' />
                </SalvModal.Footer>
              </Form>
            </SalvModal>
          </>
        );
      }}
    </Formik>
  );
};
